import React, { useState } from 'react';
import axios from 'axios';
import { Search, FileDown, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RMConsumption() {
  const [startDate, setStartDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});

  const fetchConsumption = async () => {
    if (!startDate || !endDate) {
      toast.error('Please select both start and end dates');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/reports/rm-consumption?start_date=${startDate}T00:00:00&end_date=${endDate}T23:59:59`,
        {}
      );
      setData(response.data);
      if (response.data.length === 0) {
        toast.info('No consumption data found for the selected period');
      }
    } catch (err) { console.error(err);
      toast.error('Failed to fetch consumption data');
    } finally {
      setLoading(false);
    }
  };

  const toggleRow = (idx) => {
    setExpandedRows(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  const totalConsumed = data.reduce((sum, d) => sum + d.consumed_weight, 0);
  const totalReceived = data.reduce((sum, d) => sum + d.received_weight, 0);
  const totalOpening = data.reduce((sum, d) => sum + (d.opening_weight || 0), 0);
  const totalClosing = data.reduce((sum, d) => sum + (d.closing_weight || 0), 0);
  const totalPieces = data.reduce((sum, d) => sum + d.total_pieces, 0);

  return (
    <div className="space-y-6" data-testid="rm-consumption-page">
      <div>
        <h1 className="text-4xl font-bold tracking-tight uppercase" style={{fontFamily: 'Barlow Condensed, sans-serif'}} data-testid="rm-consumption-title">RM CONSUMPTION</h1>
        <p className="text-sm text-text-muted mt-2">Raw material consumption analysis over a specified period</p>
      </div>

      {/* Filter Controls */}
      <div className="bg-white border border-border rounded-sm p-6">
        <div className="flex flex-col sm:flex-row gap-4 items-end">
          <div className="flex-1">
            <label className="text-xs font-medium uppercase tracking-wider text-text-muted mb-2 block">Start Date</label>
            <Input
              data-testid="start-date-input"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="rounded-sm"
            />
          </div>
          <div className="flex-1">
            <label className="text-xs font-medium uppercase tracking-wider text-text-muted mb-2 block">End Date</label>
            <Input
              data-testid="end-date-input"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="rounded-sm"
            />
          </div>
          <Button
            data-testid="fetch-consumption-button"
            onClick={fetchConsumption}
            disabled={loading}
            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-medium"
          >
            <Search className="w-4 h-4 mr-2" />
            {loading ? 'LOADING...' : 'GENERATE'}
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {data.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-4">
          <div className="bg-white border border-border rounded-sm p-4" data-testid="opening-weight-card">
            <p className="text-xs font-medium uppercase tracking-wider text-text-muted">Opening Stock (Weight)</p>
            <p className="text-2xl font-bold font-mono mt-1 text-blue-600">{totalOpening.toLocaleString(undefined, {maximumFractionDigits: 2})}</p>
          </div>
          <div className="bg-white border border-border rounded-sm p-4" data-testid="total-pieces-card">
            <p className="text-xs font-medium uppercase tracking-wider text-text-muted">Total Pieces Cut</p>
            <p className="text-2xl font-bold font-mono mt-1">{totalPieces.toLocaleString()}</p>
          </div>
          <div className="bg-white border border-border rounded-sm p-4" data-testid="consumed-weight-card">
            <p className="text-xs font-medium uppercase tracking-wider text-text-muted">RM Consumed (Weight)</p>
            <p className="text-2xl font-bold font-mono mt-1 text-red-600">{totalConsumed.toLocaleString(undefined, {maximumFractionDigits: 2})}</p>
          </div>
          <div className="bg-white border border-border rounded-sm p-4" data-testid="received-weight-card">
            <p className="text-xs font-medium uppercase tracking-wider text-text-muted">RM Received (Weight)</p>
            <p className="text-2xl font-bold font-mono mt-1 text-emerald-600">{totalReceived.toLocaleString(undefined, {maximumFractionDigits: 2})}</p>
          </div>
          <div className="bg-white border border-border rounded-sm p-4" data-testid="closing-weight-card">
            <p className="text-xs font-medium uppercase tracking-wider text-text-muted">Closing Stock (Weight)</p>
            <p className={`text-2xl font-bold font-mono mt-1 ${totalClosing >= 0 ? 'text-slate-800' : 'text-red-600'}`}>{totalClosing.toLocaleString(undefined, {maximumFractionDigits: 2})}</p>
          </div>
        </div>
      )}

      {/* Consumption Table */}
      {data.length > 0 && (
        <div className="bg-white border border-border rounded-sm">
          <div className="p-4 border-b border-border">
            <h2 className="text-sm font-semibold uppercase tracking-wider">Consumption by Material</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full caption-bottom text-sm">
              <thead>
                <tr className="border-b">
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider w-8"></th>
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Steel Grade</th>
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Section</th>
                  <th className="h-10 px-4 text-right align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Opening Stock</th>
                  <th className="h-10 px-4 text-right align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Pieces Cut</th>
                  <th className="h-10 px-4 text-right align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Consumed Weight</th>
                  <th className="h-10 px-4 text-right align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Received Weight</th>
                  <th className="h-10 px-4 text-right align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Closing Stock</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row, idx) => (
                  <React.Fragment key={`row-group-${idx}`}>
                    <tr
                      className="border-b transition-colors hover:bg-muted/50 cursor-pointer"
                      onClick={() => toggleRow(idx)}
                      data-testid={`consumption-row-${idx}`}
                    >
                      <td className="p-4 align-middle">
                        {row.parts.length > 0 && (
                          expandedRows[idx]
                            ? <ChevronDown className="w-4 h-4 text-text-muted" />
                            : <ChevronRight className="w-4 h-4 text-text-muted" />
                        )}
                      </td>
                      <td className="p-4 align-middle font-mono text-xs font-semibold">{row.steel_grade}</td>
                      <td className="p-4 align-middle font-mono text-xs">{row.section}</td>
                      <td className="p-4 align-middle text-right font-mono text-xs text-blue-600 font-semibold" data-testid={`opening-weight-${idx}`}>{(row.opening_weight || 0).toLocaleString(undefined, {maximumFractionDigits: 2})}</td>
                      <td className="p-4 align-middle text-right font-mono text-xs">{row.total_pieces.toLocaleString()}</td>
                      <td className="p-4 align-middle text-right font-mono text-xs text-red-600 font-semibold">{row.consumed_weight.toLocaleString(undefined, {maximumFractionDigits: 2})}</td>
                      <td className="p-4 align-middle text-right font-mono text-xs text-emerald-600">{row.received_weight.toLocaleString(undefined, {maximumFractionDigits: 2})}</td>
                      <td className="p-4 align-middle text-right font-mono text-xs font-semibold" data-testid={`closing-weight-${idx}`}>
                        <span className={(row.closing_weight || 0) >= 0 ? 'text-slate-800' : 'text-red-600'}>
                          {(row.closing_weight || 0).toLocaleString(undefined, {maximumFractionDigits: 2})}
                        </span>
                      </td>
                    </tr>
                    {/* Expanded part details */}
                    {expandedRows[idx] && row.parts.map((part, pidx) => (
                      <tr key={`part-${idx}-${pidx}`} className="bg-gray-50/50 border-b" data-testid={`part-detail-${idx}-${pidx}`}>
                        <td className="p-3"></td>
                        <td className="p-3 align-middle text-xs text-text-muted pl-8" colSpan={2}>
                          <span className="font-mono font-medium">{part.part_id}</span>
                          {part.part_name && <span className="ml-2 text-text-muted">— {part.part_name}</span>}
                        </td>
                        <td className="p-3"></td>
                        <td className="p-3 align-middle text-right font-mono text-xs text-text-muted">{part.pieces.toLocaleString()}</td>
                        <td className="p-3 align-middle text-right font-mono text-xs text-text-muted">{part.weight.toLocaleString(undefined, {maximumFractionDigits: 2})}</td>
                        <td className="p-3" colSpan={2}></td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty state */}
      {data.length === 0 && !loading && (
        <div className="bg-white border border-border rounded-sm p-12 text-center">
          <p className="text-gray-700">Select a date range and click GENERATE to view raw material consumption.</p>
        </div>
      )}
    </div>
  );
}
